import React, { useState, useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from '@fluentui/react-components';
import { Box, useFluentTokens } from '../../../theme/fluent';
import { useMarkdownComponents } from './hooks/useMarkdownComponents';

const isHtmlContent = (content: string): boolean => {
  const trimmed = content.trim();
  return /<html[\s>]/i.test(trimmed) || (trimmed.startsWith('<') && /<\/[a-z]/i.test(trimmed));
};

type MarkdownRendererProps = {
  content: string;
  className?: string;
  textColor?: string;
  maxLines?: number;
  stopPropagation?: boolean;
};

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  className,
  textColor,
  maxLines,
  stopPropagation,
}) => {
  const { tokens, modeTokens } = useFluentTokens();
  const defaultTextColor = textColor || modeTokens.colorNeutralForeground1;
  const markdownComponents = useMarkdownComponents({ tokens, modeTokens, defaultTextColor, stopPropagation });

  const [isExpanded, setIsExpanded] = useState(false);
  const [isClipped, setIsClipped] = useState(false);
  const [sanitizedHtml, setSanitizedHtml] = useState<string | null>(null);

  const contentRef = useRef<HTMLDivElement>(null);
  const isCollapsible = maxLines !== undefined;
  const showCollapsed = isCollapsible && !isExpanded;
  const isHtml = isHtmlContent(content);

  useEffect(() => {
    const element = contentRef.current;
    if (!element || !isCollapsible) return;

    // Defer initial read to rAF — avoids forced reflow in React's commit phase
    const rafId = requestAnimationFrame(() => {
      if (!isExpanded) {
        setIsClipped(Math.round(element.scrollHeight) > Math.round(element.clientHeight));
      }
    });

    // Use entry.contentRect.height from ResizeObserver — free, no extra layout read
    const observer = new ResizeObserver((entries) => {
      if (isExpanded) return;
      const entry = entries[0];
      if (entry) {
        setIsClipped(Math.round(element.scrollHeight) > Math.round(entry.contentRect.height));
      }
    });

    observer.observe(element);
    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [isCollapsible, isExpanded, content]);

  useEffect(() => {
    if (!isHtml) {
      setSanitizedHtml(null);
      return;
    }
    setSanitizedHtml(DOMPurify.sanitize(content));
  }, [isHtml, content]);

  const clampStyle = showCollapsed
    ? {
      display: '-webkit-box' as const,
      WebkitLineClamp: maxLines,
      WebkitBoxOrient: 'vertical' as const,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }
    : undefined;

  return (
    <Box className={className}>
      <style>{`
        .md-html-content p { margin-top: 0; }
        .md-html-content p:last-child { margin-bottom: 0; }
        .md-html-content ul, .md-html-content ol { margin-top: 0; margin-bottom: 0; padding-left: 1.25rem; }
        .md-html-content li { margin: 0; padding: 0; }
      `}</style>
      <div ref={contentRef} style={clampStyle}>
        {isHtml ? (
          sanitizedHtml && (
            <div
              className="md-html-content"
              style={{
                color: defaultTextColor,
                fontSize: tokens.fontSizeBase300,
                lineHeight: '1.5',
              }}
              dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
            />
          )
        ) : (
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {content}
          </ReactMarkdown>
        )}
      </div>
      {isCollapsible && showCollapsed && isClipped && (
        <Button
          appearance="transparent"
          size="small"
          style={{ padding: 0, minWidth: 0, color: modeTokens.colorBrand80 }}
          onClick={(e) => {
            if (stopPropagation) e.stopPropagation();
            setIsExpanded(true);
          }}
        >
          View more
        </Button>
      )}
      {isCollapsible && isExpanded && (
        <Button
          appearance="transparent"
          size="small"
          style={{ padding: 0, minWidth: 0, color: modeTokens.colorBrand80 }}
          onClick={(e) => {
            if (stopPropagation) e.stopPropagation();
            setIsExpanded(false);
          }}
        >
          View less
        </Button>
      )}
    </Box>
  );
};
